import { Routes, RouterModule } from '@angular/router';
import { NotFoundComponent } from './modules/shared/components/common/not-found/not-found.component';
import { unAuthorisedComponent } from './unauthorised/unauthorised.component';
import { ModuleTypes } from './modules/settings/enums/moduleTypes';
import { HistoryFeedNewComponent } from './modules/shared/components/common/history-feed-new/history-feed-new.component';
import { AccessDeniedComponent } from './access-denied/access-denied.component';
import { HistoryFeedTheme } from './modules/shared/enums/historyFeedTheme';
import { environment } from 'src/environments/environment';
import { QuicklinkStrategy } from 'ngx-quicklink';
import { UserAccountSelectComponent } from './modules/account-selection/components/user-accounts/user-accounts.component';
import { LeaveGuardService } from './modules/shared/guards/leave/leave.guard';
import { NoAccessComponent } from './no-access/no-access.component';
import { PlatformAwareAuthGuard } from './modules/auth/platform-aware-auth.guard';
import { PlanningInitResolver } from './modules/shared/resolvers/planning-init.resolver';
import { RisksInitResolver } from './modules/shared/resolvers/risks-init.resolver';
import { MyTrackInitResolver } from './modules/shared/resolvers/my-track-init.resolver';
import { EventsInitResolver } from './modules/shared/resolvers/events-init.resolver';
import { SettingsInitResolver } from './modules/shared/resolvers/settings-init.resolver';
import { DocumentManagerInitResolver } from './modules/shared/resolvers/document-manager.resolver';

const routes: Routes = [
  {
    path: 'external-access/:shareId',
    loadChildren: () => import('./modules/external/external.module').then(m => m.ExternalModule)
  },
  // Legacy route - keep for backward compatibility
  {
    path: 'external/:token',
    loadChildren: () => import('./modules/external/external.module').then(m => m.ExternalModule)
  },
  {
    path: ':accountId/userAccount',
    component: UserAccountSelectComponent,
    canActivate: [PlatformAwareAuthGuard],
    data: { moduleType: ModuleTypes.AccountSelect },
  },

  { path: ':accountId/unauthorized', component: unAuthorisedComponent, data: { title: 'Unauthorized' } },
  { path: ':accountId/no-access', component: NoAccessComponent, data: { title: 'No Access' } },
  {
    path: ':accountId/history',
    component: HistoryFeedNewComponent,
    canActivate: [PlatformAwareAuthGuard],
    data: { animation: 'activity', title: 'History Feed', bgTheme: HistoryFeedTheme.GREY, moduleType: ModuleTypes.Incidents },
  },
  { path: '', redirectTo: '/0/mytrack/planning', pathMatch: 'full' },
  { path: ':accountId/search', loadChildren: () => import('./modules/searching/searching.module').then((m) => m.SearchingModule) },
  {
    path: ':accountId/control',
    loadChildren: () => import('./modules/incidents/incidents.module').then((m) => m.IncidentsModule),
    data: { moduleType: ModuleTypes.Incidents },
  },
  {
    path: ':accountId/gantt',
    loadChildren: () => import('./modules/gantt/gantt.module').then((m) => m.GanttModule),
    data: { moduleType: ModuleTypes.Planning }, resolve: { initPlanningData: PlanningInitResolver }
  },
  {
    path: ':accountId/gantt/:id',
    loadChildren: () => import('./modules/gantt/gantt.module').then((m) => m.GanttModule),
    data: { moduleType: ModuleTypes.Planning }, resolve: { initPlanningData: PlanningInitResolver }
  },
  {
    path: ':accountId/reports',
    loadChildren: () => import('./modules/reporting/reporting.module').then((m) => m.ReportingModule),
    data: { moduleType: ModuleTypes.Reporting },
    canDeactivate: [LeaveGuardService],
  },
  {
    path: ':accountId/hub',
    loadChildren: () => import('./modules/accountHub/account-hub.module').then((m) => m.AccountHubModule),
    data: { moduleType: ModuleTypes.AccountHub },
  },
  {
    path: ':accountId/runsheets',
    loadChildren: () => import('./modules/runsheets/runsheet.module').then((m) => m.RunSheetModule),
    data: { moduleType: ModuleTypes.Runsheets },
  },
  {
    path: ':accountId/settings',
    loadChildren: () => import('./modules/settings/settings.module').then((m) => m.SettingsModule),
    data: { moduleType: ModuleTypes.Settings }, resolve: { initSettingsData: SettingsInitResolver }
  },
  {
    path: ':accountId/risk',
    loadChildren: () => import('./modules/risk/risk.module').then((m) => m.RiskModule),
    data: { moduleType: ModuleTypes.Risk }, resolve: { initRiskData: RisksInitResolver }
  },
  {
    path: ':accountId/planning',
    loadChildren: () => import('./modules/planning/planning.module').then((m) => m.PlanningModule),
    data: { moduleType: ModuleTypes.Planning }, resolve: { initPlanningData: PlanningInitResolver }
  },
  {
    path: ':accountId/sustainability',
    loadChildren: () => import('./modules/sustainability/sustainability.module').then((m) => m.SustainabilityModule),
    data: { moduleType: ModuleTypes.Sustainability },
  },
  {
    path: ':accountId/mytrack',
    loadChildren: () => import('./modules/my-track/my-track.module').then((m) => m.MyTrackModule),
    data: { moduleType: ModuleTypes.My_Track }, resolve: { initMyTrackData: MyTrackInitResolver }
  },
  {
    path: ':accountId/events',
    loadChildren: () => import('./modules/events/events.module').then((m) => m.EventModule),
    data: { moduleType: ModuleTypes.Events }, resolve: { initEventsData: EventsInitResolver }
  },
    {
    path: ':accountId/document-manager',
    loadChildren: () => import('./modules/document-manager/document-manager.module').then((m) => m.DocumentManagerModule),
    data: { moduleType: ModuleTypes.DocumentManager }, resolve: { initEventsData: DocumentManagerInitResolver }
  },
  { path: ':accountId/access-denied', component: AccessDeniedComponent, data: { title: 'Access Denied' } },
  { path: 'home', redirectTo: '/:accountId/mytrack/planning' },
  { path: 'projects', redirectTo: '/:accountId/planning/list' },
  { path: 'tasks', redirectTo: '/:accountId/planning/list' },
  { path: 'calendar', redirectTo: '/:accountId/planning/calendar' },
  { path: 'history', redirectTo: '/:accountId/planning/history' },
  {
    path: '**',
    component: NotFoundComponent,
    canActivate: [PlatformAwareAuthGuard],
    data: {
      title: '404 - Not Found',
      meta: [{ name: 'description', content: '404 - Error' }],
      links: [
        { rel: 'canonical', href: 'http://blogs.example.com/bootstrap/something' },
        { rel: 'alternate', hreflang: 'es', href: 'http://es.example.com/bootstrap-demo' },
      ],
    },
  },
];

if (environment.enableDevModule === 'true') {
  routes.push({
    path: ':accountId/devtools',
    loadChildren: () => import('./modules/devtools/devtools.module').then((m) => m.DevtoolsModule),
  });
}

export const AppRoutes = RouterModule.forRoot(routes, {
    scrollPositionRestoration: 'enabled',
    anchorScrolling: 'enabled',
    preloadingStrategy: QuicklinkStrategy
});
